import React, { useContext, useEffect, useRef, type ReactElement } from 'react';
import GoogleMapReact, { BootstrapURLKeys, Bounds, ChangeEventValue } from 'google-map-react';
import EnvironmentContext from '../../contexts/EnvironmentContext';
import Classes from './Map.module.scss';

// For legal reasons we need to limit max zoom to 16.
const MAX_ZOOM = 16;

type TMarkerProps = {
  children: ReactElement;
  lat: number;
  lng: number;
};

function Marker({ children }: TMarkerProps) {
  return children;
}

type TMarker = {
  position: number[];
  count: number;
};

type TMapProps = {
  markers?: TMarker[];
  defaultCenter: TCoordinates;
  defaultZoom: number;
  center: TCoordinates;
  zoom: number;
  onClickMap: () => void;
  onClickMarker: (clusterIndex: number) => void;
  options?: {
    draggable?: boolean;
  };
  selectedMarkerIndex: number | null;
  radius?: number;
  onDoneSettingRadius: () => void;
  onGoogleApiLoaded: ({ width }: { width: number }) => void;
  setBounds: (bounds: Bounds) => void;
};

const Map = ({
  markers,
  defaultCenter,
  defaultZoom,
  center,
  zoom,
  onClickMap,
  onClickMarker,
  options,
  selectedMarkerIndex,
  radius,
  onDoneSettingRadius,
  onGoogleApiLoaded,
  setBounds,
}: TMapProps) => {
  const {
    gmap: { apiKey: key },
  } = useContext(EnvironmentContext);
  // Unfortunately google-map-react does not provide types
  // for the map for maps arguments of the onGoogleApiLoaded
  // handler so therefore it is typed as any.
  const googleMapRef = useRef<any>(null);
  const googleMapsRef = useRef<any>(null);

  const baseMapProps = {
    bootstrapURLKeys: {
      key: key || 'mock',
    } as BootstrapURLKeys,
    defaultCenter,
    defaultZoom,
    options: {
      ...options,
      maxZoom: MAX_ZOOM,
      fullscreenControl: false,
    },
    center,
    zoom,
  };

  function handleChange(newProperties: ChangeEventValue) {
    onDoneSettingRadius();
    setBounds(newProperties.bounds);
  }

  function handleGoogleApiLoaded({ map, maps, ref }: { map: any; maps: any; ref: any }) {
    const width = ref.getBoundingClientRect().width;
    onGoogleApiLoaded({ width });
    googleMapRef.current = map;
    googleMapsRef.current = maps;
  }

  useEffect(() => {
    if (!(googleMapRef.current && googleMapsRef.current && radius)) {
      return;
    }

    const circle = new googleMapsRef.current.Circle({
      center: googleMapRef.current.getCenter(),
      radius,
    });

    googleMapRef.current.fitBounds(circle.getBounds());
  }, [onDoneSettingRadius, radius]);

  return (
    <GoogleMapReact
      {...baseMapProps}
      yesIWantToUseGoogleMapApiInternals
      onClick={onClickMap}
      onChange={handleChange}
      onGoogleApiLoaded={handleGoogleApiLoaded}
    >
      {markers?.map(({ count, position }, index) => {
        const [latitude, longitude] = position;
        const selected = index === selectedMarkerIndex;

        return (
          <Marker key={`cluster-${index}`} lat={latitude} lng={longitude}>
            <div
              data-testid="marker"
              className={`${Classes.marker} ${selected ? Classes['marker--selected'] : ''}`}
              onClick={() => onClickMarker(index)}
              onKeyUp={() => onClickMarker(index)}
            >
              {count}
            </div>
          </Marker>
        );
      })}
    </GoogleMapReact>
  );
};

export default Map;
